import { Alert, Anchor, Box, Skeleton, Text } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import type { News } from '~api';
import { formatDate } from '~lib';
import { Delayed, IllustrativeNotice } from '~portal/components/common';
import type { LiveQuery } from '~portal/data';
import { announcement } from './home.css';

/**
 * @desc The platform announcement banner (H2, Figma `426:7806`): a blue informational alert
 * drawn from the latest `platform` news item — Accent-20 fill, Accent text, info icon. Hidden when
 * there is none; while loading it holds its height so the page does not jump; a failed read is
 * said, not hidden (plan rule: no silent fallback).
 */
export function AnnouncementBanner({ query }: { query: LiveQuery<News.Announcement> }) {
  if (query.isLoading)
    return (
      <Delayed>
        <Skeleton height={64} radius="md" data-testid="announcement-loading" />
      </Delayed>
    );
  if (query.error) {
    return (
      <Alert variant="light" color="red" radius="md" title="Announcements could not be loaded">
        {query.error.message}
      </Alert>
    );
  }
  const item = query.data;
  if (!item) return null;
  return (
    <Alert
      variant="light"
      color="blue"
      radius="md"
      icon={<IconInfoCircle size={18} />}
      title={item.title}
      className={announcement}
      data-testid="home-announcement"
    >
      {item.excerpt && <Text size="sm">{item.excerpt}</Text>}
      <Text size="xs" mt={4} c="dimmed">
        {formatDate(item.publishedAt)}
        {item.url && (
          <>
            {' · '}
            <Anchor href={item.url} target="_blank" rel="noopener noreferrer" size="xs">
              Read more
            </Anchor>
          </>
        )}
      </Text>
    </Alert>
  );
}

/**
 * @desc The prototype's Illustrative notice (once per page), condensed below `sm` (H12): on a
 * 390px screen the full alert took a quarter of the first screen. Both renderings are CSS-shown or
 * hidden (`hiddenFrom` / `visibleFrom`), so no JS reads the viewport.
 */
export type HomeNoticeProps = {
  /** @desc The Recent updates events are the fixture's (the activity feed is still empty). */
  fixtureEvents: boolean;
  /** @desc The workspace name is the fixture company's (no WorkOS organisation name). */
  workspaceNameIllustrative: boolean;
};

/** @desc "a, b and c". */
const joinList = (items: readonly string[]): string =>
  items.length <= 1
    ? (items[0] ?? '')
    : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;

/**
 * @desc The notice names exactly what is live and what is badged on Home, from the same flags the
 * sections badge themselves on. The explore cards are navigation, not data, so they are neither.
 */
export const homeNoticeText = ({ fixtureEvents, workspaceNameIllustrative }: HomeNoticeProps) => {
  const placeholders = [
    ...(fixtureEvents ? ['the Recent updates events'] : []),
    'the team',
    ...(workspaceNameIllustrative ? ['the workspace name'] : []),
  ];
  const live = [
    ...(fixtureEvents ? [] : ['Recent updates']),
    'news',
    'tracked pages',
    'the announcement',
    'the analyst note',
  ];
  return `Live: ${joinList(live)}. Placeholders for design review, badged “Illustrative”: ${joinList(placeholders)}. Sample content is badged too.`;
};

export function HomeIllustrativeNotice(props: HomeNoticeProps) {
  return (
    <>
      <Box visibleFrom="sm">
        <IllustrativeNotice>{homeNoticeText(props)}</IllustrativeNotice>
      </Box>
      <Box hiddenFrom="sm">
        <Alert
          variant="light"
          color="yellow"
          radius="md"
          p="xs"
          icon={<IconInfoCircle size={16} />}
          data-testid="illustrative-notice-condensed"
        >
          <Text size="xs">Badged sections are placeholders for design review, not H2 data.</Text>
        </Alert>
      </Box>
    </>
  );
}
